import React, { useState } from 'react';
import { DialogContent, DialogActions, DialogContentText, Typography, OutlinedInput, InputAdornment, IconButton, Button } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { t } from '../../i18n/t';
import CloseableDialogComponent from './CloseableDialogComponent';


function RegexInputDialog({
  isOpen,
  onClose,
  onSave,
  title,
  placeholder,
  buttonText,
  helperComponent,
  customCheck,
}) {
  const [regex, setRegex] = useState('');
  const [error, setError] = useState('');

  const validateRegex = (pattern) => {
    try {
      // eslint-disable-next-line no-new
      new RegExp(pattern);
      return ''; // No error
    } catch {
      return t('schedule#invalid-regex');
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setRegex(value);
    // Apply custom check if provided
    if (customCheck) {
      const customError = customCheck(value);
      if (customError) {
        setError(customError);
        return;
      }
    }
    setError(validateRegex(value));
  };

  const handleClear = () => {
    setRegex('');
    setError('');
  };

  const handleSave = () => {
    if (!error) {
      onSave(regex);
      setRegex('');
      onClose();
    }
  };

  const renderClearButton = () =>
    regex && (
      <InputAdornment position="end">
        <IconButton onClick={handleClear} edge="end">
          <CloseIcon fontSize="small" />
        </IconButton>
      </InputAdornment>
    );

  return (
    <CloseableDialogComponent
      isOpen={isOpen}
      handleClose={onClose}
      maxWidth="sm"
      title={title || t('schedule#enter-expression')}
    >
      <DialogContent>
        <DialogContentText className="title ml-0 mr-0 mb-0 text-left">
          <OutlinedInput
            fullWidth
            placeholder={placeholder || t('schedule#enter-expression-holder')}
            value={regex}
            onChange={handleChange}
            error={!!error}
            endAdornment={renderClearButton()}
          />
          {error && <Typography color="error" variant="caption">{error}</Typography>}
          {helperComponent}
        </DialogContentText>
      </DialogContent>
      <DialogActions className="popup-footer justify-content-end p-0">
        <Button
          id="button-cancel"
          color="secondary"
          variant="contained"
          className="ml-3 btn-secondary"
          size="small"
          disableElevation
          onClick={onClose}
        >
          {t('schedule#cancel')}
        </Button>
        <Button
          id="button-confirm"
          variant="contained"
          color="primary"
          size="small"
          disableElevation
          onClick={handleSave}
          disabled={!regex.trim() || !!error}
        >
          {buttonText || t('schedule#save')}
        </Button>
      </DialogActions>
    </CloseableDialogComponent>
  );
}

RegexInputDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  title: PropTypes.string,
  placeholder: PropTypes.string,
  buttonText: PropTypes.string,
  helperComponent: PropTypes.node,
  customCheck: PropTypes.func,
};

RegexInputDialog.defaultProps = {
  title: '',
  placeholder: '',
  buttonText: '',
  helperComponent: null
};

export default RegexInputDialog;
